import { docReady } from "../utilities";

/**
 * Session-only show/hide for the "Join the VIP List" welcome pop-up.
 *
 * A sessionStorage flag controls visibility: the pop-up shows once per
 * browser session and is suppressed after the visitor closes it or subscribes
 * successfully. sessionStorage survives the Shopify `customer` form redirect
 * within the same tab, so after its own successful submit the pop-up opens
 * once to show the confirmation, then stays hidden.
 *
 * Braze tracking is handled by InstantPopup.ts, which flags submits and fires
 * `online_signup { source: "popup" }` on the post-submit success page (also
 * marking the pop-up dismissed).
 */
(() => {
    const POPUP_ID = "ic-welcome-popup";
    const DISMISS_KEY = "ic-welcome-popup-dismissed";
    const CONFIRM_KEY = "ic-welcome-popup-confirm";
    const SCROLL_LOCK_CLASS = "body__scroll-locked";
    const FOCUSABLE =
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const isDismissed = () => {
        try {
            return sessionStorage.getItem(DISMISS_KEY) === "1";
        } catch {
            // sessionStorage unavailable (private mode / blocked), treat as not dismissed.
            return false;
        }
    };

    const setDismissed = () => {
        try {
            sessionStorage.setItem(DISMISS_KEY, "1");
        } catch {
            /* nothing we can do if storage is blocked */
        }
    };

    const init = () => {
        const popup = document.getElementById(POPUP_ID);
        let showConfirmation = false;
        try {
            showConfirmation = sessionStorage.getItem(CONFIRM_KEY) === "1";
            sessionStorage.removeItem(CONFIRM_KEY);
        } catch {
            /* storage blocked */
        }
        if (!popup || (!showConfirmation && isDismissed())) return;

        const dialog = popup.querySelector<HTMLElement>(".welcome-popup__dialog");
        const lastFocused = document.activeElement as HTMLElement | null;
        let inerted: HTMLElement[] = [];

        const open = () => {
            popup.hidden = false;
            document.body.classList.add(SCROLL_LOCK_CLASS);
            inerted = Array.from(document.body.children).filter(
                (el): el is HTMLElement => el instanceof HTMLElement && !el.contains(popup) && !el.inert
            );
            inerted.forEach((el) => (el.inert = true));
            // Move focus into the dialog (tabindex="-1") without triggering a mobile
            // keyboard or leaving a stray focus ring on a control.
            (dialog ?? popup).focus();
        };

        const close = () => {
            if (popup.hidden) return;
            popup.hidden = true;
            document.body.classList.remove(SCROLL_LOCK_CLASS);
            setDismissed();
            inerted.forEach((el) => (el.inert = false));
            inerted = [];
            lastFocused?.focus?.();
        };

        popup
            .querySelectorAll<HTMLElement>("[data-popup-close]")
            .forEach((el) => el.addEventListener("click", close));

        popup.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                close();
                return;
            }
            // Minimal focus trap so keyboard users stay within the dialog.
            if (event.key === "Tab" && dialog) {
                const focusable = Array.from(
                    dialog.querySelectorAll<HTMLElement>(FOCUSABLE)
                ).filter((el) => el.offsetParent !== null);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        });

        open();
    };

    docReady(init);
})();
